import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Eye, EyeOff, Lock, User } from 'lucide-react'
import { api, type InventoryItemNameRow } from '@/lib/api'
import StoreHeader from '@/components/store/StoreHeader'
import StoreFooter from '@/components/store/StoreFooter'

export default function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // `?next=/checkout` etc.: return there after signing in (same-site paths only).
  const nextParam = searchParams.get('next')
  const nextPath = nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//') ? nextParam : '/'
  const [inventoryItems, setInventoryItems] = useState<InventoryItemNameRow[]>([])
  const [inventoryLoading, setInventoryLoading] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    setInventoryLoading(true)
    api.storefront
      .getInventoryItemNames()
      .then((res) => {
        if (cancelled) return
        if (res.success && res.data) {
          setInventoryItems(res.data.items)
        } else {
          setInventoryItems([])
        }
      })
      .catch(() => {
        if (!cancelled) setInventoryItems([])
      })
      .finally(() => {
        if (!cancelled) setInventoryLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const response = await api.auth.login({ email, password })

      if (response.success) {
        localStorage.setItem('srisai_access_token', response.data.access_token)
        const role = response.data.user.role
        localStorage.setItem(
          'srisai_admin_user',
          JSON.stringify({
            name: `${response.data.user.first_name} ${response.data.user.last_name}`,
            role,
            email: response.data.user.email,
          }),
        )
        if (role === 'admin') {
          localStorage.setItem('srisai_admin_token', 'authenticated')
          navigate('/admin')
        } else {
          navigate(nextPath)
        }
        return
      }
    } catch {
      // Static fallback for demo
    }

    const isStaticAdmin =
      (email === 'admin' && password === 'admin') ||
      (email === 'admin@srisaigrocery.com' && password === 'admin123')

    if (isStaticAdmin) {
      localStorage.setItem('srisai_access_token', 'static-admin')
      localStorage.setItem('srisai_admin_token', 'authenticated')
      localStorage.setItem(
        'srisai_admin_user',
        JSON.stringify({
          name: 'Administrator',
          role: 'admin',
          email: 'admin@srisaigrocery.com',
        }),
      )
      navigate('/admin')
    } else {
      setError('Invalid email or password. New customer? Create an account at the store.')
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-orange-50 via-stone-100 to-stone-300/60">
      <StoreHeader inventoryItems={inventoryItems} inventoryLoading={inventoryLoading} />

      <main className="flex flex-1 flex-col items-center justify-center px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
        <div className="w-full max-w-md animate-fade-in">
          <div className="relative rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
            <Link
              to="/"
              className="absolute right-[10px] top-[8px] z-10 text-sm font-medium text-primary-600 hover:underline sm:right-3 sm:top-2.5"
              aria-label="Back to store"
            >
              ← back
            </Link>
            <div className="mb-6 text-center sm:mb-8">
              <h1 className="font-display text-xl font-bold text-gray-800 sm:text-2xl">
                Login
              </h1>
              <p className="mt-2 text-sm text-gray-500">
                Log in to your Shree Sai Grocery account to track orders, save favorites, and more.
              </p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {error}
                </div>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Email
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field pl-10"
                    placeholder="your@email.com"
                    required
                    autoComplete="email"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pl-10 pr-10"
                    placeholder="Password"
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary flex w-full items-center justify-center gap-2 disabled:opacity-70"
              >
                {isLoading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                ) : (
                  <>
                    <Lock className="h-5 w-5" />
                    Login
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </main>

      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <StoreFooter detailsClassName="mt-8 sm:mt-10" />
      </div>
    </div>
  )
}
